import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import Badge from "@/components/base/Badge";
import Pagination from "@/components/base/Pagination";
import { EmptyState, SkeletonList } from "@/components/base/StateViews";
import CategoryHeader from "@/pages/category/components/CategoryHeader";
import { reviews } from "@/utils/data";
import { useSeo } from "@/hooks/useSeo";
import type { Review } from "@/types";

const PAGE_SIZE = 5;

const tabs: { label: string; value: "all" | Review["category"] }[] = [
  { label: "全部影评", value: "all" },
  { label: "电影影评", value: "movie" },
  { label: "电视剧影评", value: "tv" },
  { label: "专题影评", value: "special" },
];

export default function ReviewCategory() {
  useSeo({
    title: "影评专题 - 亚洲天堂",
    description:
      "亚洲天堂影评专题，汇集电影与电视剧的深度影评、观影笔记与专题解析，从多角度解读影视作品背后的故事与表达。",
    keywords: "影视影评,电影影评,电视剧影评,影评专题,亚洲天堂影评",
  });

  const [tab, setTab] = useState<"all" | Review["category"]>("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const list = useMemo(
    () => (tab === "all" ? reviews : reviews.filter((r) => r.category === tab)),
    [tab],
  );

  useEffect(() => {
    setPage(1);
  }, [tab]);

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [tab]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const pageItems = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="animate-fade-in">
      <CategoryHeader
        title="影评专题"
        description="不止于剧情简介，我们更想聊聊作品背后的表达。这里有深度解析、观影笔记与专题观察。"
        icon="ri-chat-quote-line"
        breadcrumb={<Breadcrumb items={[{ label: "影评" }]} />}
      />

      <div className="mx-auto w-full max-w-site px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 inline-flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {tabs.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setTab(item.value)}
              className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                tab === item.value
                  ? "bg-primary-500 text-background-50"
                  : "text-foreground-600 hover:text-primary-700"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {loading ? (
          <SkeletonList count={PAGE_SIZE} />
        ) : list.length === 0 ? (
          <EmptyState
            title="该分类暂无影评"
            description="换个标签看看吧，或者浏览全部影评。"
            actionLabel="查看全部影评"
            onAction={() => setTab("all")}
            icon="ri-chat-quote-line"
          />
        ) : (
          <>
            <div className="mb-4 text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{list.length}</strong> 篇影评
            </div>
            <div className="flex flex-col gap-4">
              {pageItems.map((review) => (
                <Link
                  key={review.id}
                  to={`/detail/review/${review.id}`}
                  className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:border-accent-200 sm:flex-row"
                >
                  <span className="relative block h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-28 sm:w-44">
                    <img
                      src={review.cover}
                      alt={review.title}
                      title={`${review.title} - 影评 - 亚洲天堂`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </span>
                  <span className="flex flex-1 flex-col">
                    <span className="mb-1.5 flex flex-wrap items-center gap-2">
                      <Badge tone={review.category === "special" ? "accent" : "primary"} className="text-[10px]">
                        {review.category === "movie" ? "电影影评" : review.category === "tv" ? "电视剧影评" : "专题影评"}
                      </Badge>
                      <span className="text-xs text-foreground-400">
                        关联作品：{review.workTitle}
                      </span>
                    </span>
                    <h3 className="mb-1 line-clamp-2 text-base font-semibold text-foreground-900 transition-colors group-hover:text-accent-600">
                      {review.title}
                    </h3>
                    <p className="mb-3 line-clamp-2 text-sm leading-relaxed text-foreground-500">{review.excerpt}</p>
                    <span className="mt-auto flex flex-wrap items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1.5">
                        <img src={review.avatar} alt={review.author} className="h-5 w-5 rounded-full object-cover object-top" />
                        {review.author}
                      </span>
                      <span>{review.date}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-thumb-up-line" aria-hidden="true"></i>
                        {review.likes}
                      </span>
                    </span>
                  </span>
                </Link>
              ))}
            </div>
            <div className="mt-8">
              <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}